import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BriefcaseIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  StarIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  UserIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import api from '../../services/api';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import NoticeModal from '../NoticeModal';
import ReviewDetailModal from '../ReviewDetailModal';

const ContractorDashboard = () => {
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const navigate = useNavigate();

  const handleProjectStatusClick = (status) => {
    // 프로젝트 목록 페이지로 이동하면서 스크롤을 맨 위로 리셋
    navigate(`/projects?status=${status}`);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };
  
  // 소장 통계
  const { data: stats } = useQuery(
    'contractorStats',
    () => api.get('/contractor/stats').then(res => res.data),
    { refetchInterval: 30000 }
  );

  // 최근 공지사항
  const { data: recentNotices } = useQuery(
    'contractorNotices',
    () => api.get('/notices?limit=3').then(res => res.data?.notices || [])
  );



  const formatCurrency = (amount) => {
    if (!amount) return '0원';
    const billion = Math.floor(amount / 100000000);
    const million = Math.floor((amount % 100000000) / 10000);
    
    if (billion > 0) {
      return `${billion}억${million > 0 ? ` ${million}만원` : '원'}`;
    } else if (million > 0) {
      return `${million}만원`;
    } else {
      return new Intl.NumberFormat('ko-KR').format(amount) + '원';
    }
  };

  return (
    <div className="space-y-6 pb-20">
      {/* 상단 헤더 - 개선된 디자인 */}
      <div className="relative bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 rounded-2xl p-6 text-white overflow-hidden">
        {/* 배경 패턴 */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-white rounded-full"></div>
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-white rounded-full"></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">{stats?.contractor?.name ? `${stats.contractor.name} 소장님` : '소장님'} 👋</h1>
              <p className="text-purple-200">{format(new Date(), 'yyyy년 M월 d일 EEEE', { locale: ko })}</p>
              {stats?.contractor?.company_name && (
                <p className="text-sm text-purple-300 mt-1">{stats.contractor.company_name}</p>
              )}
            </div>
            <Link to="/projects" className="flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-lg hover:bg-white/30 transition-all hover:scale-105">
              <BriefcaseIcon className="h-5 w-5" />
              <span className="text-sm font-medium">프로젝트</span>
            </Link>
          </div>
        </div>
      </div>

      {/* 긴급 알림 - 개선된 디자인 */}
      {stats?.projectCounts?.received > 0 && (
        <div className="relative bg-gradient-to-r from-red-500 via-red-600 to-red-700 rounded-2xl p-5 text-white shadow-2xl overflow-hidden transform hover:scale-[1.02] transition-all duration-300">
          {/* 애니메이션 배경 */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-red-600 opacity-30 animate-pulse"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full animate-ping"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full animate-ping animation-delay-1000"></div>
          </div>
          
          <Link to="/projects?status=received" className="relative z-10 flex items-center justify-between group">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
                <div className="relative bg-white/20 backdrop-blur p-3 rounded-full group-hover:scale-110 transition-transform">
                  <ExclamationTriangleIcon className="h-7 w-7 text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">
                  {stats?.projectCounts?.received}개의 새 프로젝트
                </h3>
                <p className="text-red-100 text-sm flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  {stats?.unreadProjects > 0 ? (
                    <>그 중 {stats.unreadProjects}개 미확인!</>
                  ) : (
                    <>모두 확인 • 상세보기</>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <span className="text-4xl font-bold block group-hover:scale-110 transition-transform">{stats?.projectCounts?.received}</span>
                <span className="text-xs text-red-200">접수</span>
              </div>
              <ChevronRightIcon className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      )}

      {/* 기타 알림 섹션 - 개선된 디자인 */}
      {(stats?.pendingMessagesCount > 0 || stats?.unpaidCommission > 0) && (
        <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-2xl p-5 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {stats?.pendingMessagesCount > 0 && (
                  <Link 
                    to={stats?.firstPendingMessageProjectId 
                      ? `/projects/${stats.firstPendingMessageProjectId}?tab=chat` 
                      : "/projects?has_message=true"} 
                    className="group flex items-center justify-between p-4 bg-white rounded-xl border-2 border-orange-200 hover:border-orange-300 hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                  >
                    <div className="flex items-center gap-3">
                      <ChatBubbleLeftRightIcon className="h-5 w-5 text-orange-600" />
                      <span className="text-sm font-semibold text-gray-900">답변 대기</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-orange-600">{stats.pendingMessagesCount}</span>
                      <span className="text-xs text-gray-500">건</span>
                      <ChevronRightIcon className="h-4 w-4 text-orange-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                )}
                {stats?.unpaidCommission > 0 && (
                  <Link 
                    to="/contractor/commission" 
                    className="group flex items-center justify-between p-4 bg-white rounded-xl border-2 border-red-200 hover:border-red-300 hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                  >
                    <div className="flex items-center gap-3">
                      <CurrencyDollarIcon className="h-5 w-5 text-red-600" />
                      <span className="text-sm font-semibold text-gray-900">수수료 미납</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-red-600">{stats.unpaidCommission}</span>
                      <span className="text-xs text-gray-500">건</span>
                      <ChevronRightIcon className="h-4 w-4 text-red-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                )}
          </div>
        </div>
      )}

      {/* 주요 지표 카드 - 디자인 개선 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="group relative bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-blue-200/20 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <div className="bg-blue-200/50 p-2 rounded-lg group-hover:bg-blue-300/50 transition-colors">
                <DocumentTextIcon className="h-4 w-4 text-blue-700" />
              </div>
              <span className="text-xs text-blue-600 font-medium px-2 py-1 bg-blue-100 rounded-full">오늘</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 group-hover:scale-110 transition-transform origin-left">{stats?.todayAssigned || 0}</p>
            <p className="text-xs text-gray-600 mt-1">배정된 프로젝트</p>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-amber-200/20 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <div className="bg-amber-200/50 p-2 rounded-lg group-hover:bg-amber-300/50 transition-colors">
                <ClockIcon className="h-4 w-4 text-amber-700" />
              </div>
              <span className="text-xs text-amber-600 font-medium px-2 py-1 bg-amber-100 rounded-full">진행중</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 group-hover:scale-110 transition-transform origin-left">{stats?.inProgress || 0}</p>
            <p className="text-xs text-gray-600 mt-1">계약 프로젝트</p>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-200/20 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <div className="bg-emerald-200/50 p-2 rounded-lg group-hover:bg-emerald-300/50 transition-colors">
                <BanknotesIcon className="h-4 w-4 text-emerald-700" />
              </div>
              <span className="text-xs text-emerald-600 font-medium px-2 py-1 bg-emerald-100 rounded-full">이번달</span>
            </div>
            <p className="text-xl font-bold text-gray-900 group-hover:scale-110 transition-transform origin-left">{stats?.monthlyCompleted || 0}건</p>
            <p className="text-xs text-emerald-600 font-medium">{formatCurrency(stats?.monthlyRevenue)}</p>
          </div>
        </div>

        <div className="group relative bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-16 h-16 bg-purple-200/20 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-500"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-2">
              <div className="bg-purple-200/50 p-2 rounded-lg group-hover:bg-purple-300/50 transition-colors">
                <StarIcon className="h-4 w-4 text-purple-700" />
              </div>
              <span className="text-xs text-purple-600 font-medium px-2 py-1 bg-purple-100 rounded-full">평점</span>
            </div>
            <div className="flex items-center gap-1">
              <p className="text-2xl font-bold text-gray-900 group-hover:scale-110 transition-transform origin-left">{(stats?.rating || 0).toFixed(1)}</p>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <StarSolid key={i} className={`h-3 w-3 transition-all duration-300 ${i < Math.floor(stats?.rating || 0) ? 'text-yellow-400 group-hover:scale-125' : 'text-gray-300'}`} />
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-1">({stats?.reviewCount || 0}개 리뷰)</p>
          </div>
        </div>
      </div>

      {/* 최근 리뷰 섹션 - 리뷰가 있을 때만 표시 */}
      {stats?.recentReviews?.length > 0 && (
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <StarIcon className="h-5 w-5 text-purple-600" />
            최근 고객 리뷰
          </h3>
          <div className="space-y-3">
            {stats.recentReviews.slice(0, 5).map((review, index) => (
              <div 
                key={index} 
                className="bg-white rounded-lg p-4 border border-purple-100 cursor-pointer hover:bg-purple-50 hover:border-purple-200 transition-colors"
                onClick={() => {
                  setSelectedReview(review);
                  setShowReviewModal(true);
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{review.customer_name}</p>
                    <p className="text-xs text-gray-500 hover:text-purple-600 transition-colors">{review.project_title}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <StarSolid key={i} className={`h-4 w-4 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`} />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-gray-700 line-clamp-2">{review.comment}</p>
                <p className="text-xs text-gray-400 mt-1">{format(new Date(review.created_at), 'MM월 dd일')}</p>
                <div className="mt-2 text-xs text-purple-600 hover:text-purple-700 font-medium">클릭하여 자세히 보기 →</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 실시간 활동 & 공지사항 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 최근 공지사항 - 개선된 디자인 */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-blue-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                </div>
                최근 공지사항
              </h3>
              <Link to="/notices" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 group">
                전체보기 
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {recentNotices?.length > 0 ? (
              recentNotices.map((notice) => (
                <div 
                  key={notice.id} 
                  onClick={() => setSelectedNotice(notice)}
                  className="group bg-white rounded-xl p-4 hover:bg-blue-50 transition-all cursor-pointer hover:shadow-md transform hover:-translate-y-0.5"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {notice.is_important ? (
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      ) : (
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-sm truncate group-hover:text-blue-700 transition-colors">
                        {notice.title}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        {format(new Date(notice.created_at), 'MM월 dd일 HH:mm')}
                      </p>
                    </div>
                    <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">등록된 공지사항이 없습니다.</p>
              </div>
            )}
          </div>
        </div>

        {/* 내 프로젝트 현황 - 개선된 디자인 */}
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-indigo-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <BriefcaseIcon className="h-5 w-5 text-indigo-600" />
              </div>
              내 프로젝트 현황
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div 
              onClick={() => handleProjectStatusClick('received')}
              className="group relative flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-blue-100 hover:border-blue-200 overflow-hidden cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">접수</p>
                  <p className="text-xs text-gray-500">새로 배정됨</p>
                </div>
              </div>
              <div className="relative flex items-center gap-1">
                <span className="text-3xl font-bold text-blue-600 group-hover:scale-110 transition-transform">{stats?.projectCounts?.received || 0}</span>
                <span className="text-sm text-gray-500">건</span>
              </div>
            </div>

            <div 
              onClick={() => handleProjectStatusClick('review')}
              className="group relative flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-amber-100 hover:border-amber-200 overflow-hidden cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-50 to-orange-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <ClockIcon className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">검토중</p>
                  <p className="text-xs text-gray-500">견적 준비</p>
                </div>
              </div>
              <div className="relative flex items-center gap-1">
                <span className="text-3xl font-bold text-amber-600 group-hover:scale-110 transition-transform">{stats?.projectCounts?.review || 0}</span>
                <span className="text-sm text-gray-500">건</span>
              </div>
            </div>

            <div 
              onClick={() => handleProjectStatusClick('contract')}
              className="group relative flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-emerald-100 hover:border-emerald-200 overflow-hidden cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-50 to-green-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <BriefcaseIcon className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">계약</p>
                  <p className="text-xs text-gray-500">진행중</p>
                </div>
              </div>
              <div className="relative flex items-center gap-1">
                <span className="text-3xl font-bold text-emerald-600 group-hover:scale-110 transition-transform">{stats?.projectCounts?.contract || 0}</span>
                <span className="text-sm text-gray-500">건</span>
              </div>
            </div>

            <div 
              onClick={() => handleProjectStatusClick('completed')}
              className="group relative flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-gray-200 overflow-hidden cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-slate-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <CheckCircleIcon className="h-6 w-6 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">완료</p>
                  <p className="text-xs text-gray-500">전체 완료</p>
                </div>
              </div>
              <div className="relative flex items-center gap-1">
                <span className="text-3xl font-bold text-gray-600 group-hover:scale-110 transition-transform">{stats?.projectCounts?.completed || 0}</span>
                <span className="text-sm text-gray-500">건</span>
              </div>
            </div>
          </div>
        </div>

        {/* 수수료 & 정산 - 개선된 디자인 */}
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="p-6 bg-white/70 backdrop-blur border-b border-emerald-100">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <div className="bg-emerald-100 p-2 rounded-lg">
                <CurrencyDollarIcon className="h-5 w-5 text-emerald-600" />
              </div>
              수수료 & 정산
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="relative p-4 bg-white rounded-xl border border-emerald-200 overflow-hidden group hover:shadow-md transition-all">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-50 to-teal-50 opacity-50"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-emerald-700 font-semibold">미납 수수료</p>
                  {stats?.unpaidCommission > 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-medium rounded-full animate-pulse">
                      지급 필요
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-emerald-900">
                  {formatCurrency(stats?.unpaidAmount || 0)}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-emerald-600">{stats?.unpaidCommission || 0}건</p>
                  <div className="w-16 h-1 bg-emerald-200 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: '30%' }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* 프로젝트 성사율 추가 */}
            <div className="relative p-4 bg-white rounded-xl border border-blue-200 overflow-hidden group hover:shadow-md transition-all">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 opacity-50"></div>
              <div className="relative">
                <p className="text-sm text-blue-700 font-semibold mb-2">프로젝트 성사율</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-blue-900">{stats?.projectCounts?.successRate || 0}%</p>
                    <p className="text-xs text-blue-600 mt-1">
                      전체 {stats?.projectCounts?.total || 0}건
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-blue-600 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                        계약: {stats?.projectCounts?.contract || 0}건
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        완료: {stats?.projectCounts?.completed || 0}건
                      </div>
                    </div>
                  </div>
                </div>
                {/* 프로그레스 바 */}
                <div className="mt-3 h-2 bg-blue-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-1000"
                    style={{ width: `${stats?.projectCounts?.successRate || 0}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {stats?.commissionSettings && (
              <div className="relative p-4 bg-white rounded-xl border border-purple-200 overflow-hidden group hover:shadow-md transition-all">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-50 to-pink-50 opacity-50"></div>
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-700 font-semibold mb-1">수수료 타입</p>
                      <p className="text-lg font-bold text-purple-900">
                        {stats.commissionSettings.fee_type === 'monthly' ? '월정액' : '프로젝트별'}
                      </p>
                    </div>
                    <div className="bg-purple-100 p-3 rounded-xl">
                      <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                  {stats.commissionSettings.fee_type === 'monthly' && (
                    <p className="text-xs text-purple-600 mt-2 bg-purple-100 px-2 py-1 rounded inline-block">
                      매월 {stats.commissionSettings.payment_day}일 · {formatCurrency(stats.commissionSettings.monthly_amount)}
                    </p>
                  )}
                </div>
              </div>
            )}

            <Link to="/contractor/commission" className="block w-full text-center py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all text-sm font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group">
              수수료 납부하기 
              <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>
        </div>
      </div>

      {/* 월별 실적 차트 - 개선된 디자인 */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-white/70 backdrop-blur p-6 border-b border-purple-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ArrowTrendingUpIcon className="h-5 w-5 text-purple-600" />
              월별 완료 실적
            </h3>
            <span className="text-sm text-purple-600 font-medium">
              최근 6개월
            </span>
          </div>
        </div>
        <div className="p-6">
          {stats?.monthlyCompletedChart?.length > 0 ? (
            <>
              {/* 차트 영역 */}
              <div className="h-64 flex items-end justify-between gap-3 mb-4">
                {stats.monthlyCompletedChart.map((month, index) => {
                  const maxCount = Math.max(...stats.monthlyCompletedChart.map(m => m.count));
                  const heightPercent = maxCount > 0 ? (month.count / maxCount) * 100 : 0;
                  const isHighest = month.count === maxCount && month.count > 0;
                  
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center group cursor-pointer">
                      <div className="relative w-full flex flex-col items-center">
                        {/* 툴팁 */}
                        <div className="absolute -top-10 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10">
                          <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap">
                            <div className="font-bold">{month.count}건</div>
                            <div className="text-gray-300">{month.month}월</div>
                          </div>
                          <div className="w-2 h-2 bg-gray-900 rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2"></div>
                        </div>
                        
                        {/* 바 차트 */}
                        <div 
                          className={`w-full rounded-t-xl transition-all duration-700 relative overflow-hidden ${
                            isHighest 
                              ? 'bg-gradient-to-t from-purple-600 to-purple-400 shadow-lg' 
                              : 'bg-gradient-to-t from-indigo-500 to-indigo-400'
                          } hover:shadow-xl transform hover:-translate-y-1`}
                          style={{ 
                            height: `${heightPercent * 2}px`,
                            minHeight: month.count > 0 ? '20px' : '0px',
                            maxHeight: '200px'
                          }}
                        >
                          {/* 애니메이션 효과 */}
                          <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity"></div>
                          {isHighest && (
                            <div className="absolute top-2 left-1/2 -translate-x-1/2">
                              <TrophyIcon className="h-4 w-4 text-white/80" />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* 월 라벨 */}
                      <div className="mt-3 text-center">
                        <span className="text-sm font-medium text-gray-700">{month.month}월</span>
                        {month.count > 0 && (
                          <p className="text-xs text-gray-500 mt-0.5">{month.count}건</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* 통계 요약 */}
              <div className="bg-white/50 rounded-xl p-4 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-xs text-gray-600">총 완료</p>
                  <p className="text-lg font-bold text-gray-900">
                    {stats.monthlyCompletedChart.reduce((sum, m) => sum + m.count, 0)}건
                  </p>
                </div>
                <div className="text-center border-x border-gray-200">
                  <p className="text-xs text-gray-600">월 평균</p>
                  <p className="text-lg font-bold text-purple-600">
                    {(stats.monthlyCompletedChart.reduce((sum, m) => sum + m.count, 0) / 6).toFixed(1)}건
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600">최고 기록</p>
                  <p className="text-lg font-bold text-green-600">
                    {Math.max(...stats.monthlyCompletedChart.map(m => m.count))}건
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-pulse flex space-x-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="w-12 bg-gray-200 rounded-t-lg" style={{ height: `${(i + 1) * 30}px` }}></div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-4">데이터를 불러오는 중...</p>
            </div>
          )}
        </div>
      </div>

      {/* 빠른 액션 - 개선된 디자인 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link to="/projects" className="group relative overflow-hidden bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>
          
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-14 h-14 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <DocumentTextIcon className="h-8 w-8" />
            </div>
            <p className="font-bold text-lg">내 프로젝트</p>
            <p className="text-xs text-blue-100 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">프로젝트 관리 →</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </Link>
        
        <Link to="/contractor/stats" className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>
          
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-14 h-14 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <ArrowTrendingUpIcon className="h-8 w-8" />
            </div>
            <p className="font-bold text-lg">내 통계</p>
            <p className="text-xs text-emerald-100 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">실적 분석 →</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </Link>
        
        <Link to="/contractor/commission" className="group relative overflow-hidden bg-gradient-to-br from-purple-500 via-purple-600 to-pink-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>
          
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-14 h-14 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <CurrencyDollarIcon className="h-8 w-8" />
            </div>
            <p className="font-bold text-lg">수수료</p>
            <p className="text-xs text-purple-100 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">정산 관리 →</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </Link>
        
        <Link to="/profile" className="group relative overflow-hidden bg-gradient-to-br from-amber-500 via-amber-600 to-orange-700 text-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
          {/* 배경 장식 */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full -ml-8 -mb-8"></div>
          
          <div className="relative z-10">
            <div className="bg-white/20 backdrop-blur w-14 h-14 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <UserIcon className="h-8 w-8" />
            </div>
            <p className="font-bold text-lg">프로필</p>
            <p className="text-xs text-amber-100 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">내 정보 →</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </Link>
      </div>

      {/* 공지사항 모달 */}
      {selectedNotice && (
        <NoticeModal
          notice={selectedNotice}
          onClose={() => setSelectedNotice(null)}
        />
      )}

      {/* 리뷰 상세 모달 */}
      <ReviewDetailModal
        review={selectedReview}
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setSelectedReview(null);
        }}
      />
    </div>
  );
};

export default ContractorDashboard;